import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { obsAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { canObserve } from '../../lib/permissions';
import { BUCKETS } from '../../theme';
import { Spinner } from '../../components/ui';
import TabBar from '../../components/TabBar';

const PERIODS = [
  { k:'12w',  l:'12 weeks'     },
  { k:'term', l:'Term vs term' },
  { k:'year', l:'Year vs year' },
];

/** Colour by how healthy the signal is, not by bucket identity. */
const pctColor = (pct) =>
  pct >= 70 ? 'var(--teal)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';

/** A delta of null means there was nothing to compare against — say so
 *  rather than showing a fabricated zero. */
function Delta({ value, suffix = '' }) {
  if (value === null || value === undefined) {
    return <span className="dl dl-none">No comparison</span>;
  }
  const cls = value > 0 ? 'dl-up' : value < 0 ? 'dl-down' : 'dl-flat';
  const sign = value > 0 ? '↑ ' : value < 0 ? '↓ ' : '';
  return <span className={`dl ${cls}`}>{sign}{Math.abs(value)}{suffix}</span>;
}

/** Sparkline over the weeks that actually have notes. */
function Sparkline({ series }) {
  const pts = series.filter(p => p.note_count > 0);
  if (pts.length < 2) {
    return <div className="spark-empty">Not enough weekly data to plot a trend yet.</div>;
  }
  const W = 330, H = 48, pad = 6;
  const max = Math.max(...pts.map(p => p.pct), 100);
  const x = i => pad + (i * (W - pad * 2)) / (pts.length - 1);
  const y = p => H - pad - ((p.pct / max) * (H - pad * 2));
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p)}`).join(' ');
  const last = pts[pts.length - 1];

  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <path d={`${line} L${x(pts.length - 1)},${H - pad} L${pad},${H - pad}Z`}
            fill="var(--amber)" opacity="0.07" />
      <path d={line} fill="none" stroke="var(--amber)" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(pts.length - 1)} cy={y(last)} r="4" fill="var(--amber)" />
    </svg>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view,   setView]   = useState('pulse');
  const [period, setPeriod] = useState('term');
  const [selBar, setSelBar] = useState(null);

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    obsAPI.dashboard({ period })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError(err?.response?.data?.detail || 'Could not load the dashboard.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const footnote = data
    ? `Student names anonymised across all dashboards. Based on ${data.total_notes} evidence ` +
      `notes across ${data.obs_count} observations in this period.`
    : '';

  return (
    <div className="screen" id="s-school-pulse">
      <div className="dh">
        <div className="dh-top">
          <button className="dh-back" onClick={() => navigate('/')}>‹</button>
          <span className="dh-title">Dashboard</span>
          <span className="dh-scope">{data?.school?.name || ''}</span>
        </div>
        <div className="vtabs">
          {[{k:'pulse',l:'Learning pulse'},{k:'story',l:'Story view'}].map(t => (
            <div key={t.k} className={`vt${view===t.k?' on':''}`} onClick={() => setView(t.k)}>{t.l}</div>
          ))}
        </div>
      </div>

      <div className="ptabs">
        {PERIODS.map(p => (
          <div key={p.k} className={`pt${period===p.k?' on':''}`} onClick={() => setPeriod(p.k)}>
            {p.l}
          </div>
        ))}
      </div>

      <div className="scroll">
        <div className="pad">
          {loading && (
            <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}>
              <Spinner />
            </div>
          )}

          {!loading && error && <div className="dash-empty">{error}</div>}

          {!loading && !error && data && !data.has_data && (
            <div className="dash-empty">
              No observations recorded in this period. Once observations are completed,
              the patterns will appear here.
            </div>
          )}

          {!loading && !error && data?.has_data && view === 'pulse' && (
            <>
              {/* Headline stats */}
              <div className="dstat-grid">
                {[
                  { n: data.stats.observations.value,  l:'Observations',  d: data.stats.observations.delta,  suffix:'' },
                  { n: `${data.stats.debrief_rate.value}%`, l:'Debrief rate', d: data.stats.debrief_rate.delta, suffix:'%' },
                  { n: `${data.stats.staff_reached.value}%`, l:'Staff reached', d: data.stats.staff_reached.delta, suffix:'%',
                    sub: data.stats.staff_reached.detail },
                ].map((s,i) => (
                  <div key={i} className="dstat">
                    <div className="dstat-n">{s.n}</div>
                    <div className="dstat-l">{s.l}</div>
                    {s.sub && <div className="dstat-sub">{s.sub}</div>}
                    <Delta value={s.d} suffix={s.suffix} />
                  </div>
                ))}
              </div>

              {/* Milestones */}
              {data.milestones.map((m,i) => (
                <div key={i} className="dmile">
                  <div className="dmile-bar" />
                  <div className="dmile-label">{m.label}</div>
                  <div className="dmile-date">
                    {new Date(m.date).toLocaleDateString(undefined,{ day:'numeric', month:'short' })}
                  </div>
                </div>
              ))}

              {/* Buckets */}
              <div className="dcard">
                <div className="dcard-title">Learning patterns — by bucket</div>
                <div className="dcard-sub">% of evidence showing learning happening · student names anonymised</div>
                {data.buckets.map(b => (
                  <div key={b.key}>
                    <div className="bbar-item" style={{ cursor:'pointer' }}
                         onClick={() => setSelBar(selBar===b.key ? null : b.key)}>
                      <div className="bbar-label">{b.label}</div>
                      <div className="bbar-track">
                        <div className="bbar-fill" style={{ width:`${b.pct}%`, background:pctColor(b.pct) }} />
                      </div>
                      <div className="bbar-pct" style={{ color:pctColor(b.pct) }}>{b.pct}%</div>
                    </div>
                    {selBar === b.key && (
                      <div className="bbar-detail">
                        <strong>
                          {b.label}: {b.pct}% this period
                          {b.prev !== null && <> vs {b.prev}% last period ({b.delta > 0 ? '+' : ''}{b.delta} pts)</>}
                        </strong>
                        <br />
                        {b.one_line} Based on {b.note_count} evidence {b.note_count === 1 ? 'note' : 'notes'}.
                        {b.prev === null && ' No data last period, so no change is shown.'}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Focus bucket */}
              {data.focus && (
                <div className="dcard">
                  <div className="dfocus-head">
                    <div className="dfocus-dot" style={{ background: BUCKETS[data.focus.key]?.dot || 'var(--amber)' }} />
                    <div className="dfocus-name">{data.focus.label}</div>
                    <span className="dfocus-pill">School focus this term</span>
                  </div>
                  {data.focus.now_pct === null ? (
                    <div className="spark-empty">No evidence tagged to this bucket in this period yet.</div>
                  ) : (
                    <>
                      <div className="dfocus-stats">
                        {[
                          { n:`${data.focus.start_pct}%`, l:'Period start', c:'var(--text3)' },
                          { n:`${data.focus.now_pct}%`,   l:'Now',          c:'var(--amber)' },
                          { n: data.focus.grown === null ? '—'
                              : `${data.focus.grown > 0 ? '+' : ''}${data.focus.grown}`,
                            l:'Points grown', c: (data.focus.grown ?? 0) >= 0 ? 'var(--teal)' : 'var(--red)' },
                        ].map((item,i) => (
                          <div key={i} className="dfocus-stat" style={{ borderLeft: i>0 ? '1px solid var(--border)' : '' }}>
                            <div className="dfocus-stat-n" style={{ color:item.c }}>{item.n}</div>
                            <div className="dfocus-stat-l">{item.l}</div>
                          </div>
                        ))}
                      </div>
                      <Sparkline series={data.focus.series} />
                    </>
                  )}
                </div>
              )}

              {/* Observation health */}
              <div className="dcard">
                <div className="dcard-title" style={{ fontSize:13, marginBottom:12 }}>Observation health</div>
                {data.health.map(r => (
                  <div key={r.label} className="dhealth-row">
                    <div className="dhealth-l">{r.label}</div>
                    <div className="dhealth-track">
                      <div className="dhealth-fill" style={{ width:`${r.pct}%`, background:pctColor(r.pct) }} />
                    </div>
                    <div className="dhealth-pct" style={{ color:pctColor(r.pct) }}>{r.pct}%</div>
                  </div>
                ))}
              </div>

              <div className="dash-foot">{footnote}</div>
            </>
          )}

          {!loading && !error && data?.has_data && view === 'story' && (
            <>
              <div className="dstory-intro">
                {data.window.start} to {data.window.end} · {data.school.name} ·{' '}
                {data.obs_count} observations. Here is what your data says about learning
                in this period.
              </div>

              {!data.has_comparison && (
                <div className="dash-note">
                  There is no earlier period to compare against yet, so this reads as a
                  snapshot rather than a trend.
                </div>
              )}

              {data.stories.cards.map((st, i) => {
                const color = st.tone === 'neg' ? 'var(--red)' : 'var(--teal)';
                return (
                  <div key={i} className="dstory">
                    <div className="dstory-bar" style={{ background:color }} />
                    <div className="dstory-body">
                      <div className="dstory-eyebrow" style={{ color }}>{st.eyebrow}</div>
                      <div className="dstory-headline">{st.headline}</div>
                      <div className="dstory-para">{st.para}</div>
                      <div className="dstory-data">
                        {st.data.map((d,j) => (
                          <div key={j} className="dstory-stat"
                               style={{ borderLeft: j>0 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                            <div className="dstory-stat-n" style={{ color: j===1 ? 'var(--text3)' : color }}>{d.n}</div>
                            <div className="dstory-stat-l">{d.l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {data.stories.watch.length > 0 && (
                <div className="dcard">
                  <div className="dwatch-lbl">Worth watching</div>
                  {data.stories.watch.map((w,i) => (
                    <div key={i} className="dwatch-row"
                         style={{ borderBottom: i < data.stories.watch.length-1 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                      <div className="dwatch-dot"
                           style={{ background: w.tone === 'neg' ? 'var(--red)' : 'var(--amber)' }} />
                      <div className="dwatch-text">{w.text}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="dash-foot">
                Story generated from {data.total_notes} evidence notes across {data.obs_count}{' '}
                observations. Student names anonymised throughout.
              </div>
            </>
          )}
        </div>
      </div>

      <TabBar current="dashboard" />
    </div>
  );
}
